import { Link } from "react-router-dom";
import { useCart } from "@/store/cart";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";
import { useWishlist } from "@/hooks/useWishlist";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Heart } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  stock: number;
  category: string | null;
  on_sale?: boolean | null;
  sale_price?: number | null;
}

export const ProductCard = ({ product }: { product: Product }) => {
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const addItem = useCart((s) => s.addItem);
  const setOpen = useCart((s) => s.setOpen);
  const { isWished, toggle } = useWishlist();
  const wished = isWished(product.id);

  const effectivePrice =
    product.on_sale && product.sale_price ? Number(product.sale_price) : Number(product.price);
  const outOfStock = product.stock <= 0;
  const discount =
    product.on_sale && product.sale_price && Number(product.price) > 0
      ? Math.round((1 - Number(product.sale_price) / Number(product.price)) * 100)
      : 0;

  const quickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    addItem({
      id: product.id,
      name: product.name,
      price: effectivePrice,
      image_url: product.image_url,
      stock: product.stock,
    });
    toast.success("Added to cart", { description: product.name });
    setOpen(true);
  };

  const toggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(product.id, product.name);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-card shadow-soft transition-all duration-300 hover:shadow-hover hover:-translate-y-1.5"
    >
      <div className="relative aspect-square overflow-hidden bg-secondary/40 p-3">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-muted-foreground/40">
            <ShoppingBag className="h-12 w-12" />
          </div>
        )}

        {/* Subtle hover overlay */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        {/* SALE badge — top-left */}
        {product.on_sale && (
          <span className="absolute left-3 top-3 rounded-full bg-destructive/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-destructive-foreground shadow-soft backdrop-blur">
            {discount > 0 ? `-${discount}%` : "Sale"}
          </span>
        )}
        {outOfStock && (
          <span className="absolute left-3 bottom-3 rounded-full bg-foreground/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-background backdrop-blur">
            Sold out
          </span>
        )}

        {/* Wishlist heart — top-right */}
        <button
          type="button"
          onClick={toggleWishlist}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          className={cn(
            "absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-background/85 backdrop-blur shadow-soft transition-all duration-300 hover:scale-110 active:scale-90 hover:bg-background",
            wished && "text-destructive",
          )}
        >
          <Heart
            className={cn("h-4 w-4 transition-all duration-300", wished && "fill-current scale-110")}
          />
        </button>

        {/* Add to cart — fade in on hover */}
        <div className="absolute inset-x-3 bottom-3 opacity-0 translate-y-3 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
          <Button
            onClick={quickAdd}
            size="sm"
            disabled={outOfStock}
            className="w-full rounded-full shadow-card"
          >
            <ShoppingBag className="mr-1.5 h-3.5 w-3.5" />
            Add to cart
          </Button>
        </div>
      </div>

      <div className="space-y-1 p-4">
        {product.category && (
          <span className="inline-block rounded-full bg-secondary/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {product.category}
          </span>
        )}
        <h3 className="line-clamp-2 pt-0.5 font-medium leading-tight transition-colors group-hover:text-primary">
          {product.name}
        </h3>
        <div className="flex items-baseline gap-2 pt-1">
          <span
            className={cn(
              "font-display text-lg font-semibold",
              product.on_sale && "text-destructive",
            )}
          >
            {formatCurrency(effectivePrice, symbol)}
          </span>
          {product.on_sale && product.sale_price && (
            <span className="text-sm text-muted-foreground line-through">
              {formatCurrency(Number(product.price), symbol)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
